import { createTheme } from '@mui/material/styles';

// Professional color palette for business applications
const colors = {
  // Primary WhatsApp brand colors
  primary: {
    50: '#E8F5E8',
    100: '#C8E6C9',
    200: '#A5D6A7',
    300: '#81C784',
    400: '#66BB6A',
    500: '#25D366', // Main WhatsApp green
    600: '#21C05D',
    700: '#1BAA52',
    800: '#169546',
    900: '#0D7332',
  },
  
  // Secondary professional colors
  secondary: {
    50: '#E0F2F1',
    100: '#B2DFDB',
    200: '#80CBC4',
    300: '#4DB6AC',
    400: '#26A69A',
    500: '#128C7E', // WhatsApp dark green
    600: '#00796B',
    700: '#00695C',
    800: '#00564A',
    900: '#004D40',
  },
  
  // Professional grays for business applications
  gray: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
  
  // Semantic colors with accessibility compliance
  success: {
    50: '#E8F5E8',
    100: '#C8E6C9',
    500: '#4CAF50',
    600: '#43A047',
    700: '#388E3C',
  },
  
  warning: {
    50: '#FFF8E1',
    100: '#FFECB3',
    500: '#FF9800',
    600: '#FB8C00',
    700: '#F57C00',
  },
  
  error: {
    50: '#FFEBEE',
    100: '#FFCDD2',
    500: '#F44336',
    600: '#E53935',
    700: '#D32F2F',
  },
  
  info: {
    50: '#E3F2FD',
    100: '#BBDEFB',
    500: '#2196F3',
    600: '#1E88E5',
    700: '#1976D2',
  },
  
  // Professional surface colors
  surface: {
    paper: '#FFFFFF',
    background: '#FAFAFA',
    elevated: '#FFFFFF',
    overlay: 'rgba(0, 0, 0, 0.5)',
  }
};

// 8-point grid spacing system
const spacing = (factor) => `${8 * factor}px`;

// Professional typography scale
const typography = {
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  
  // Display typography for major headings
  h1: {
    fontSize: '3.5rem', // 56px
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.02em',
    '@media (max-width:960px)': {
      fontSize: '2.75rem', // 44px
    },
    '@media (max-width:600px)': {
      fontSize: '2.25rem', // 36px
    },
  },
  
  h2: {
    fontSize: '2.75rem', // 44px
    fontWeight: 600,
    lineHeight: 1.25,
    letterSpacing: '-0.01em',
    '@media (max-width:960px)': {
      fontSize: '2.25rem', // 36px
    },
    '@media (max-width:600px)': {
      fontSize: '1.875rem', // 30px
    },
  },
  
  h3: {
    fontSize: '2.125rem', // 34px
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
    '@media (max-width:960px)': {
      fontSize: '1.875rem', // 30px
    },
    '@media (max-width:600px)': {
      fontSize: '1.5rem', // 24px
    },
  },
  
  h4: {
    fontSize: '1.5rem', // 24px
    fontWeight: 600,
    lineHeight: 1.35,
    letterSpacing: 0,
    '@media (max-width:600px)': {
      fontSize: '1.25rem', // 20px
    },
  },
  
  h5: {
    fontSize: '1.25rem', // 20px
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: 0,
  },
  
  h6: {
    fontSize: '1.125rem', // 18px
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: 0,
  },
  
  // Body typography
  body1: {
    fontSize: '1rem', // 16px
    fontWeight: 400,
    lineHeight: 1.6,
    letterSpacing: '0.01em',
  },
  
  body2: {
    fontSize: '0.875rem', // 14px
    fontWeight: 400,
    lineHeight: 1.6,
    letterSpacing: '0.01em',
  },
  
  // UI typography
  subtitle1: {
    fontSize: '1rem', // 16px
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.01em',
  },
  
  subtitle2: {
    fontSize: '0.875rem', // 14px
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.01em',
  },
  
  caption: {
    fontSize: '0.75rem', // 12px
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.02em',
  },
  
  overline: {
    fontSize: '0.75rem', // 12px
    fontWeight: 600,
    lineHeight: 1.5,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
  },
  
  button: {
    fontSize: '0.875rem', // 14px
    fontWeight: 600,
    lineHeight: 1.5,
    letterSpacing: '0.02em',
    textTransform: 'none', // Professional appearance
  },
};

// Professional animation timing and easing
const transitions = {
  duration: {
    shortest: 150,
    shorter: 200,
    short: 250,
    standard: 300,
    complex: 375,
    enteringScreen: 225,
    leavingScreen: 195,
  },
  easing: {
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
  },
};

// Enhanced shadow system for depth
const shadows = [
  'none',
  '0px 1px 3px rgba(0, 0, 0, 0.12), 0px 1px 2px rgba(0, 0, 0, 0.24)', // elevation 1
  '0px 3px 6px rgba(0, 0, 0, 0.16), 0px 3px 6px rgba(0, 0, 0, 0.23)', // elevation 2
  '0px 10px 20px rgba(0, 0, 0, 0.19), 0px 6px 6px rgba(0, 0, 0, 0.23)', // elevation 3
  '0px 14px 28px rgba(0, 0, 0, 0.25), 0px 10px 10px rgba(0, 0, 0, 0.22)', // elevation 4
  '0px 19px 38px rgba(0, 0, 0, 0.30), 0px 15px 12px rgba(0, 0, 0, 0.22)', // elevation 5
];

// Create the comprehensive theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      ...colors.primary,
      main: colors.primary[500],
    },
    secondary: {
      ...colors.secondary,
      main: colors.secondary[500],
    },
    error: {
      ...colors.error,
      main: colors.error[500],
    },
    warning: {
      ...colors.warning,
      main: colors.warning[500],
    },
    info: {
      ...colors.info,
      main: colors.info[500],
    },
    success: {
      ...colors.success,
      main: colors.success[500],
    },
    grey: colors.gray,
    background: {
      default: colors.surface.background,
      paper: colors.surface.paper,
    },
    text: {
      primary: colors.gray[900],
      secondary: colors.gray[600],
      disabled: colors.gray[400],
    },
    divider: colors.gray[200],
    action: {
      active: colors.gray[600],
      hover: 'rgba(0, 0, 0, 0.04)',
      selected: 'rgba(0, 0, 0, 0.08)',
      disabled: colors.gray[300],
      disabledBackground: colors.gray[100],
    },
  },
  
  typography,
  
  spacing,
  
  shape: {
    borderRadius: 8, // Professional rounded corners
  },
  
  shadows,
  
  transitions,
  
  // Custom component styling
  components: {
    // AppBar styling
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.surface.paper,
          color: colors.gray[900],
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12)',
          borderBottom: `1px solid ${colors.gray[200]}`,
        },
      },
    },
    
    // Paper component styling
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none', // Remove default gradient
        },
        elevation0: {
          border: `1px solid ${colors.gray[200]}`,
        },
      },
    },
    
    // Button styling
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px',
          transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.16)',
          },
        },
        contained: {
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.12)',
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.16)',
          },
        },
      },
    },
    
    // Card styling
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${colors.gray[200]}`,
          transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    
    // TextField styling
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-1px)',
            },
            '&.Mui-focused': {
              transform: 'translateY(-1px)',
              boxShadow: '0px 4px 8px rgba(37, 211, 102, 0.16)',
            },
          },
        },
      },
    },
    
    // Chip styling
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
      },
    },
    
    // Avatar styling
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
      },
    },
  },
});

export default theme;